import Layout from "../../components/Layout";
import { ArrowLeft } from "lucide-react";
import useScrollReveal from "../../hooks/useScrollReveal";
import useCounter from "../../hooks/useCounter";
import ProjectTOC from "../../components/ProjectTOC";
import ComparisonBlock from "../../components/ComparisonBlock";
import ProjectNav from "../../components/ProjectNav";

export default function I3FY() {
  useScrollReveal();

  const modules = useCounter(20);
  const examples = useCounter(50);

  const sections = [
    { id: "overview", label: "Overview" },
    { id: "architecture", label: "Architecture" },
    { id: "contributions", label: "Contributions" },
    { id: "design", label: "Design Philosophy" },
    { id: "diagram", label: "System Diagram" },
    { id: "compare", label: "Static vs Interactive" },
    { id: "download", label: "Download" },
  ];

  return (
    <Layout>
      <ProjectTOC sections={sections} />

      <div className="max-w-4xl mx-auto py-20 px-6 space-y-24 font-inter">

        {/* Back */}
        <a
          href="/projects"
          className="inline-flex items-center gap-2 text-sm opacity-60 hover:opacity-100 transition"
        >
          <ArrowLeft size={16} />
          Back to Projects
        </a>

        {/* Hero */}
        <header className="space-y-6 reveal">
          <h1 className="text-5xl md:text-6xl font-semibold tracking-tight leading-[1.1]">
            I3FY &mdash; Interactive Coding <br /> Education Platform
          </h1>

          <p className="text-lg md:text-xl opacity-70 max-w-2xl">
            A modular learning platform engineered to make{" "}
            <span className="highlight-word">coding education simple, structured, and intuitive</span>, 
            combining explanations with{" "}
            <span className="highlight-word">interactive code execution</span>.
          </p>

          <p className="text-sm uppercase tracking-[0.25em] opacity-50">
            Web Platform • Interactive Runner • React
          </p>
        </header>

        {/* Metrics */}
        <section className="grid md:grid-cols-3 gap-10 reveal">
          {[
            { label: "Modules", value: `${modules}+` },
            { label: "Examples", value: `${examples}+` },
            { label: "Open-Source", value: "Yes" },
          ].map((item) => (
            <div
              key={item.label}
              className="metric rounded-3xl border border-black/10 dark:border-white/10 
                         bg-white/50 dark:bg-white/5 backdrop-blur-xl p-10 text-center shadow-sm"
            >
              <p className="text-5xl md:text-6xl font-semibold mb-3 tracking-tight">
                {item.value}
              </p>
              <p className="text-sm uppercase tracking-wide opacity-70">{item.label}</p>
            </div>
          ))}
        </section>

        <div id="overview" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />

        {/* OVERVIEW */}
        <section className="space-y-6 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">Overview</h2>

          <p className="text-lg opacity-80 leading-relaxed">
            I3FY is a coding-education platform designed to reduce friction in learning by providing 
            <span className="highlight-word"> concise explanations</span>,
            <span className="highlight-word"> structured learning flows</span>, and 
            <span className="highlight-word"> interactive code examples</span> inside a minimal, distraction-free UI.
          </p>

          <p className="text-lg opacity-80 leading-relaxed">
            Instead of presenting static tutorials, I3FY builds an{" "}
            <span className="highlight-word">interactive pipeline</span> where every concept can be tested live.
          </p>
        </section>

        {/* ARCHITECTURE */}
        <div id="architecture" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />

        <section className="space-y-8 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">Platform Architecture</h2>

          <ul className="list-disc ml-6 space-y-4 text-lg opacity-80 leading-relaxed">
            <li>
              <span className="font-semibold highlight-word">Module Engine:</span>{" "}
              Dynamically loads modules from JSON configs, enabling scalable content updates.
            </li>

            <li>
              <span className="font-semibold highlight-word">Interactive Runner:</span>{" "}
              Syntax-highlighted editor with preview panel for evaluating code in real-time.
            </li>

            <li>
              <span className="font-semibold highlight-word">Content Renderer:</span>{" "}
              Markdown-like renderer for clean explanations and examples.
            </li>

            <li>
              <span className="font-semibold highlight-word">Progress Tracker:</span>{" "}
              Local persistence layer for tracking lesson completion.
            </li>

            <li>
              <span className="font-semibold highlight-word">UI Layer:</span>{" "}
              React component architecture with reusable blocks for modules, examples, and quizzes.
            </li>
          </ul>
        </section>

        {/* CONTRIBUTIONS */}
        <div id="contributions" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />

        <section
          className="rounded-3xl p-10 bg-white/30 dark:bg-white/5 backdrop-blur-xl
                     border border-black/10 dark:border-white/10 space-y-8 reveal"
        >
          <h3 className="text-3xl font-semibold">My Technical Contributions</h3>

          <ul className="list-disc ml-6 space-y-3 text-lg opacity-80 leading-relaxed">
            <li>Designed and developed the full React front-end architecture</li>
            <li>Built the dynamic module loader & content configuration system</li>
            <li>Implemented syntax-highlighted code runner with live evaluation</li>
            <li>Created 20+ modules with examples, diagrams, and quizzes</li>
            <li>Optimised layout and interactions for student-first reading</li>
            <li>Open-sourced the entire platform with documentation</li>
          </ul>
        </section>

        {/* DESIGN PHILOSOPHY */}
        <div id="design" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />

        <section className="space-y-8 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">Design Philosophy</h2>

          <p className="text-lg opacity-80 leading-relaxed">
            Inspired by Apple’s Human Interface Guidelines, the platform focuses on:
          </p>

          <ul className="list-disc ml-6 space-y-3 text-lg opacity-80 leading-relaxed">
            <li>Clean typography optimised for long reading</li>
            <li>Generous vertical spacing to reduce cognitive load</li>
            <li>Minimal colour use — content takes priority</li>
            <li>Interactive examples first, explanations second</li>
            <li>Consistent hierarchy across modules</li>
          </ul>
        </section>

        {/* SYSTEM DIAGRAM */}
        <div id="diagram" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />

        <section className="space-y-6 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">System Diagram</h2>

          <div
            className="p-6 rounded-3xl bg-white/20 dark:bg-white/5 
                       backdrop-blur-xl border border-black/10 
                       dark:border-white/10 font-mono text-sm 
                       whitespace-pre overflow-x-auto reveal"
          >
{`
MODULES → MODULE LOADER → RUNTIME ENGINE → CODE RUNNER → OUTPUT PANEL

[JSON] → [Parser] → [Component Mapping] → [Executor] → [Live Preview]
`}
          </div>
        </section>

        {/* COMPARISON */}
        <div id="compare" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />

        <section className="space-y-6 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">Static vs Interactive Learning</h2>

          <ComparisonBlock
            aTitle="Interactive Mode"
            bTitle="Static Tutorials"
            aContent={
              <p className="text-lg opacity-80">
                • Real-time feedback<br />
                • Faster concept reinforcement<br />
                • Students experiment instantly<br />
                • Higher retention for beginners
              </p>
            }
            bContent={
              <p className="text-lg opacity-80">
                • Slow feedback loops<br />
                • Harder to visualise behaviour<br />
                • Passive reading<br />
                • Less engaging for new learners
              </p>
            }
          />
        </section>

        {/* DOWNLOAD */}
        <div id="download" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />

        <section className="reveal">
          <a
            href="/i3fy.pdf"
            className="text-lg underline opacity-70 hover:opacity-100 transition"
            download
          >
            Download full documentation (PDF)
          </a>
        </section>

        {/* NAVIGATION */}
        <ProjectNav
          prev={{ href: "/projects/ai-manufacturing-morex", label: "← MOREX Automation" }}
          next={null}
        />
      </div>
    </Layout>
  );
}
